<template>
	<div class="c-profile-billing">
		<div
			v-if="offer && offer.status === 'unclaimed'"
			class="c-profile-billing__offer"
		>
			<h3>Your 50% offer is ready</h3>
			<p>
				You have 50% off for your first 3 months. Claim it to start your
				24-hour checkout window.
			</p>
			<button
				type="button"
				class="btn btn-primary"
				:disabled="claimingOffer"
				@click="claimOffer"
			>
				{{ claimingOffer ? 'Claiming offer...' : 'Claim offer' }}
			</button>
			<p v-if="offerError" class="c-profile-billing__error">{{ offerError }}</p>
		</div>
		<div
			v-else-if="offer && offer.status === 'active'"
			class="c-profile-billing__offer"
		>
			<h3>🎁 Here's a gift for you</h3>
			<p>
				Subscription to a plan for half price for your first three months. Offer
				ends
				{{ expiryText }}.
			</p>
			<button type="button" class="btn btn-primary" @click="offerOpen = true">
				Choose a monthly plan
			</button>
		</div>
		<template v-else-if="!offerLoading">
			<p v-if="offerError" class="c-profile-billing__error">{{ offerError }}</p>
			<WorkspaceBilling></WorkspaceBilling>
		</template>

		<Modal
			v-model="offerOpen"
			:closable="true"
			class="c-profile-billing__modal"
		>
			<template v-slot:title>
				<h2>🎁 50% offer claimed!</h2>
			</template>
			<p>
				Your discount will be applied when you subscribe. Choose a plan before
				{{ expiryText }}.
			</p>
			<p v-if="remainingText">Time remaining: {{ remainingText }}</p>
			<p v-if="offerError" class="c-profile-billing__error">{{ offerError }}</p>
			<PricingTable
				v-if="prices.length"
				:prices="prices"
				:monthlyOnly="true"
				:offerPercent="50"
				@onUpgrade="startCheckout"
			></PricingTable>
			<button
				v-if="!prices.length"
				type="button"
				class="btn btn-primary"
				@click="loadOffer"
			>
				Retry loading plans
			</button>
			<button type="button" class="btn" @click="offerOpen = false">
				Maybe later
			</button>
		</Modal>
	</div>
</template>

<script>
import WorkspaceBilling from '@swipekit/components/workspace/billing.vue';
import PricingTable from '@swipekit/components/pricing-table';
import Modal from '@swipekit/components/modal';
import api from '@swipekit/lib/api';

export default {
	components: { WorkspaceBilling, PricingTable, Modal },
	data: function () {
		return {
			offer: null,
			offerLoading: true,
			offerOpen: false,
			offerError: '',
			claimingOffer: false,
			prices: [],
			now: Date.now(),
			clock: null,
		};
	},
	computed: {
		isAuth: function () {
			return this.$store.getters['users/isAuth'];
		},
		expiryText: function () {
			if (!this.offer || !this.offer.expiresAt) {
				return '';
			}
			return new Date(this.offer.expiresAt).toLocaleString();
		},
		remainingText: function () {
			if (!this.offer || !this.offer.expiresAt) {
				return '';
			}
			const remaining = new Date(this.offer.expiresAt).getTime() - this.now;
			if (remaining <= 0) {
				return '';
			}
			const totalMinutes = Math.ceil(remaining / (60 * 1000));
			const hours = Math.floor(totalMinutes / 60);
			const minutes = totalMinutes % 60;
			return `${hours}h ${minutes}m`;
		},
	},
	watch: {
		isAuth: function (isAuth) {
			if (isAuth) {
				this.loadOffer();
			}
		},
	},
	methods: {
		async claimOffer() {
			this.claimingOffer = true;
			this.offerError = '';
			try {
				this.offer = await api.users.claimDiscountOffer();
				if (!this.offer || this.offer.status !== 'active') {
					this.offerError = 'This offer is unavailable.';
					return;
				}
				try {
					this.prices = await api.api.getPrices();
				} catch (err) {
					this.prices = [];
					this.offerError = 'Could not load plans. Please try again.';
				}
				this.offerOpen = true;
			} catch (err) {
				this.offerError =
					err.response && err.response.data && err.response.data.message
						? err.response.data.message
						: 'This offer is unavailable.';
			} finally {
				this.claimingOffer = false;
			}
		},
		async loadOffer() {
			const wasOpen = this.offerOpen;
			this.offerLoading = true;
			this.offerError = '';
			try {
				if (this.$route.query.claimOffer === '1') {
					this.offer = await api.users.claimDiscountOffer();
				} else {
					this.offer = await api.users.getDiscountOffer();
				}
			} catch (err) {
				if (
					this.$route.query.claimOffer === '1' ||
					this.$route.query.offer === 'claimed'
				) {
					this.offerError =
						err.response && err.response.data && err.response.data.message
							? err.response.data.message
							: 'This offer is unavailable.';
				}
				this.offer = null;
				this.offerOpen = false;
				this.offerLoading = false;
				return;
			}
			if (this.offer.status === 'active') {
				try {
					this.prices = await api.api.getPrices();
				} catch (err) {
					this.prices = [];
					this.offerError = 'Could not load plans. Please try again.';
				}
				this.offerOpen =
					wasOpen ||
					this.$route.query.claimOffer === '1' ||
					this.$route.query.offer === 'claimed';
			} else {
				this.offerOpen = false;
			}
			this.offerLoading = false;
		},
		async startCheckout(plan) {
			this.offerError = '';
			try {
				const result = await api.users.upgrade({
					plan: plan.plan,
					frequency: 'monthly',
					priceId: plan.priceId,
					useDiscountOffer: true,
				});
				if (result && result.url) {
					window.location = result.url;
				}
			} catch (err) {
				const message =
					err.response && err.response.data && err.response.data.message
						? err.response.data.message
						: 'Could not start checkout. Please try again.';
				await this.loadOffer();
				this.offerError = message;
				if (this.offer && this.offer.status === 'active') {
					this.offerOpen = true;
				}
			}
		},
	},
	mounted: function () {
		if (this.isAuth) {
			this.loadOffer();
		}
		this.clock = setInterval(() => {
			this.now = Date.now();
			if (
				this.offer &&
				this.offer.status === 'active' &&
				this.now >= new Date(this.offer.expiresAt).getTime()
			) {
				this.offerOpen = false;
				this.loadOffer();
			}
		}, 30000);
	},
	beforeDestroy: function () {
		clearInterval(this.clock);
	},
};
</script>

<style lang="scss">
.c-profile-billing {
	&__offer {
		padding: var(--spacer);
		border: 2px solid var(--color-primary);
		border-radius: var(--border-radius);
		text-align: center;
	}
	&__error {
		color: var(--color-danger);
	}
	&__modal .modal-content {
		width: min(1100px, 95vw);
		max-height: 95vh;
		overflow-y: auto;
	}
	&__modal {
		.c-pricing-table {
			margin-bottom: 1rem;
			&__body {
				grid-template-columns: repeat(3, minmax(0, 1fr));
			}
		}
	}
	@media (max-width: 800px) {
		&__modal .c-pricing-table__body {
			grid-template-columns: 1fr;
		}
	}
}
</style>
